/**
 * EhrExportGuide Component
 *
 * Accordion-based guide showing how to export patient data from popular EHRs.
 * Displayed on the Import Patients upload page to help users prepare their files.
 */

import { useMemo, useState } from 'react';
import {
  CalendarMonth as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useStatsigClient } from '@statsig/react-bindings';

import {
  EHR_GUIDES,
  type ExportSection,
  type ExportStep,
} from './ehrExportGuideData';

// ─── Sub-components ─────────────────────────────────────────────────────────

function ExportStepList({ steps }: { steps: ExportStep[] }) {
  return (
    <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1 } }}>
      {steps.map((step, i) => (
        <li key={i}>
          <Typography variant="body2">{step.text}</Typography>
          {/* TODO: Add screenshots/GIFs for each step
          {step.mediaPlaceholder && (
            <Box
              sx={{
                mt: 1,
                mb: 1.5,
                p: 2,
                border: '2px dashed',
                borderColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {step.mediaPlaceholder}
              </Typography>
            </Box>
          )} */}
        </li>
      ))}
    </Box>
  );
}

function ExportSectionBlock({ section }: { section: ExportSection }) {
  return (
    <Box sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {section.title}
      </Typography>
      <ExportStepList steps={section.steps} />
      {section.warning && (
        <Alert severity="info" variant="outlined" sx={{ mt: 1.5, fontSize: '0.8125rem' }}>
          {section.warning}
        </Alert>
      )}
    </Box>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function EhrExportGuide() {
  const theme = useTheme();
  const { client } = useStatsigClient();
  const [expanded, setExpanded] = useState<string | false>(false);

  const bookingUrl = useMemo(() => {
    const config = client.getDynamicConfig('data_migration_booking_link');
    return (config.value as { url?: string }).url || '';
  }, [client]);

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{fontWeight: 600,  mb: 0.5 }}>
        How to Export from Your EHR
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select your current EHR below for step-by-step instructions on exporting your patient data.
      </Typography>

      <Alert icon={<InfoIcon />} severity="info" sx={{ mb: 2 }}>
        We support importing <strong>patient demographics</strong>, <strong>insurance info</strong>, and <strong>appointments</strong>. <strong>Medications</strong>, <strong>diagnoses</strong>, and <strong>allergies</strong> are also imported when your export includes them (e.g. CCDA / Summary of Care XML). Clinical notes are not imported.
      </Alert>

      {EHR_GUIDES.map((guide) => (
        <Accordion
          key={guide.name}
          expanded={expanded === guide.name}
          onChange={handleChange(guide.name)}
          variant="outlined"
          disableGutters
          sx={{
            '&:before': { display: 'none' },
            borderRadius: expanded === guide.name ? '8px !important' : undefined,
            mb: 1,
            '&.Mui-expanded': { mb: 1 },
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 2.5,
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                gap: 1.5,
                my: 1.5,
              },
            }}
          >
            <Typography variant="subtitle1" sx={{fontWeight: 600,  flexShrink: 0 }}>
              {guide.name}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
              {guide.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.6875rem',
                    borderColor: alpha(theme.palette.text.secondary, 0.3),
                    color: 'text.secondary',
                  }}
                />
              ))}
            </Stack>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
            {/* Overview */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {guide.overview}
            </Typography>

            {/* Export sections */}
            {guide.sections.map((section, i) => (
              <ExportSectionBlock key={i} section={section} />
            ))}

            {/* Official guide link */}
            {guide.officialGuideUrl && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Link
                  href={guide.officialGuideUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                  }}
                >
                  View official export guide
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Link>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Assistance CTA */}
      {bookingUrl && (
        <Paper
          variant="outlined"
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Need assistance?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Book a free session and we&apos;ll help you migrate your data.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<CalendarIcon />}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Book a session
          </Button>
        </Paper>
      )}
    </Box>
  );
}
